import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
    ArrowLeftCircle, Mail, Phone, Building2, CalendarClock, ChevronDown,
    Power, PowerOff, Loader2, Info, ShieldAlert,
} from 'lucide-react';
import {
    fetchMocPatient, fetchMocActivity, setMocPatientStatus, resetMocPatientDetail,
} from '../../redux/mocSlice';
import { getPatientById } from '../../redux/patientSlice';
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import PatientProfileTab from '../../components/patientTabs/PatientProfileTab';
import DependenciesReadOnly from '../../components/patientTabs/DependenciesReadOnly';
import HealthRecordReadOnly from '../../components/patientTabs/HealthRecordReadOnly';
import MocCustomPriceTab from '../../components/patientTabs/MocCustomPriceTab';
import MocAccountPasswordTab from '../../components/patientTabs/MocAccountPasswordTab';
import MocBillsTab from '../../components/patientTabs/MocBillsTab';
import MocActivityPanel, { DEFAULT_LIMIT } from './MocActivityPanel';
import { MocStatusBadge, VerifiedMark, formatDate } from './mocCommon';
import useCanManage from '../../hooks/useCanManage';

// ONE MOC IDENTITY — the patient profile, built to read as the SAME SCREEN as the
// tenant app's (Frontend-web/src/pages/profile/PatientProfile.jsx): the same
// gradient back button and heading, the same pill tab rail with the sliding
// highlight, the same mobile dropdown, the same six tabs in the same order.
//
// ---------------------------------------------------------------------------
// WHERE THE HOSPITAL DROPDOWN LIVES NOW, AND WHY IT MOVED
// ---------------------------------------------------------------------------
// It used to sit in the header, above everything, because a person WAS one
// `patients` row per hospital: their address at Chitra and their address at
// Apollo were two different rows, so "which hospital are you looking at?" had
// to be answered before any tab meant anything.
//
// One-patient-row (owner, 22 Sep 2026) ends that. There is ONE person row —
// `patientId = MOC00007` — carrying the whole profile, and a hospital holds an
// ALLOCATION against it (`patient_hospitals`: its own patient number, assigned
// doctor, price, note). So:
//
//   * Profile and Dependencies (family) are PROPERTIES OF THE PERSON. They
//     read `GET /patients/:mocAccountId` and `/patients/:id/family` with the
//     person id and are the same answer at every hospital — asking which
//     hospital first would have been a question with one answer.
//   * Custom price and Platform activity are CLINICAL / HOSPITAL data, keyed
//     by (patient, tenant). The picker therefore sits with THEM, right above
//     the tab body, where the choice it makes is visible in what changes.
//   * Bills are clinical too, but their endpoint takes no tenant and this
//     caller is unscoped, so that tab answers for EVERY hospital and says so
//     instead of carrying a picker that would filter nothing (see it below).
//
// The picker is also no longer a way to get a different patientId: every tab
// passes the SAME person id. Only the tenant filter changes.
//
// PROFILE IS READ-ONLY HERE, and not because Super Admin is denied the fields
// (utils/patientAccess.js has treated super_admin as connected since 26 Aug, so
// the whole profile comes back). It is read-only because the row belongs to the
// PATIENT: `PUT /patients/:id` answers 409 MOC_OWNED_FIELD on any change to a
// claimed person's identity/contact/profile field, whoever asks. An Edit button
// here would offer a save the API refuses.
//
// A person with no allocation yet (registered in the patient app, not yet at
// any hospital) still gets a full Profile and Dependencies — only the clinical
// tabs say there is nothing to show.
//
// Platform activity is the one view the tenant app cannot have: the
// cross-hospital timeline, the hospital list and break-glass.

// Medical History and Files tabs retired (owner, 2026-09-02): the Platform
// activity tab already lists every appointment, and the Files feature was
// deleted platform-wide with its toolbox API.
const TAB_KEYS = {
    profile: 'Profile',
    dependencies: 'Dependencies',
    // PERSON-LEVEL, like the two above it: the health record has no tenantId,
    // so it sits before the clinical tabs and takes no hospital.
    health: 'Health record',
    price: 'Custom Price',
    bills: 'Bills',
    password: 'Password',
    activity: 'Platform activity',
};

// --- MODULE-LEVEL SUBCOMPONENTS -------------------------------------------
// An inline component is a new type on every render: React remounts its subtree
// and any input inside loses focus after one keystroke.

const InfoRow = ({ icon, children }) => {
    const Icon = icon;
    return (
        <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
            <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {children}
        </div>
    );
};

// The hospital's own patient number for this person, which
// GET /moc-admin/patients/:id sends on every allocation. `tenantPatientCode`
// is the same value under the key the older clients read, kept for one release.
const codeAt = (hospital) => hospital?.hospitalPatientCode || hospital?.tenantPatientCode || null;

const HospitalPicker = ({ hospitals, current, onPick, open, onToggle, label, noneLabel, hospitalWord }) => (
    <div className="relative" data-testid="moc-hospital-picker">
        <span className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">{label}</span>
        <button
            type="button"
            data-testid="moc-hospital-picker-button"
            onClick={onToggle}
            disabled={hospitals.length === 0}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${hospitals.length === 0
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                : 'border-[#4ABDBD]/40 bg-[#4ABDBD]/10 text-[#2D8A8A] hover:bg-[#4ABDBD]/20 cursor-pointer'}`}
        >
            <Building2 className="w-4 h-4 flex-shrink-0" />
            <span className="max-w-[16rem] truncate">
                {current ? (current.hospitalName || hospitalWord) : noneLabel}
            </span>
            {hospitals.length > 0 && (
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
            )}
        </button>

        {open && hospitals.length > 0 && (
            <div className="absolute left-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
                {hospitals.map((h) => (
                    <button
                        key={h.tenantId}
                        type="button"
                        data-testid={`moc-hospital-option-${h.tenantId}`}
                        onClick={() => onPick(h.tenantId)}
                        className={`block w-full border-b border-gray-100 px-4 py-3 text-left text-sm transition-colors last:border-b-0 cursor-pointer ${current?.tenantId === h.tenantId
                            ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white'
                            : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                        <span className="block truncate font-semibold">{h.hospitalName || hospitalWord}</span>
                        {/* The HOSPITAL's own number for this person, when the
                            API sends it. Never the person id: that is the same
                            string under every hospital now, so printing it here
                            would read as "they have four ids" when they have
                            one. Falls back to the date the allocation was made. */}
                        <span className={`block truncate text-[10px] ${current?.tenantId === h.tenantId ? 'text-white/70' : 'text-gray-400'}`}>
                            {codeAt(h) || (h.linkedAt ? formatDate(h.linkedAt) : '')}
                        </span>
                    </button>
                ))}
            </div>
        )}
    </div>
);

const NoHospitalNotice = ({ title, body }) => (
    <div data-testid="moc-no-hospital" className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
        <Info className="mx-auto h-7 w-7 text-gray-300" />
        <p className="mt-3 text-sm font-semibold text-[#1a365d]">{title}</p>
        <p className="mx-auto mt-1 max-w-lg text-xs leading-relaxed text-gray-500">{body}</p>
    </div>
);

export default function MocPatientDetail() {
    const { t } = useTranslation(['patientprofilepage']);
    const { mocAccountId } = useParams();
    const dispatch = useDispatch();
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('patients.manage');
    const navigate = useNavigate();

    const {
        detail, detailLoading, detailError, statusUpdating,
        activity, activityLoading, activityError,
    } = useSelector((s) => s.moc);
    const { selectedPatient, isLoading: patientLoading } = useSelector((s) => s.patient);

    // ?tab=password|activity|bills|… — the patients-table row shortcuts land
    // straight on a tab. Unknown values fall back to Profile.
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(() => {
        const asked = searchParams.get('tab');
        return (asked && TAB_KEYS[asked]) ? TAB_KEYS[asked] : TAB_KEYS.profile;
    });
    const [mobileTabOpen, setMobileTabOpen] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [tenantId, setTenantId] = useState(null);
    const [priceDoctorId, setPriceDoctorId] = useState(null);

    const [kind, setKind] = useState('');
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    // Guards the auto-pick below so it only fires for a freshly loaded identity —
    // otherwise every detail refresh would yank the operator back to hospital #1.
    const autoPickedFor = useRef(null);

    useEffect(() => {
        dispatch(resetMocPatientDetail());
        setKind('');
        setOffset(0);
        setTenantId(null);
        setPriceDoctorId(null);
        setActiveTab(TAB_KEYS.profile);
        autoPickedFor.current = null;
        dispatch(fetchMocPatient(mocAccountId));
    }, [dispatch, mocAccountId]);

    // tenantId rides along (owner, 2026-09-02): picking a hospital must
    // actually narrow the doctors, counts and timeline to it, not just move
    // the highlight ring — the backend filters server-side.
    useEffect(() => {
        dispatch(fetchMocActivity({ mocAccountId, kind, limit, offset, tenantId }));
    }, [dispatch, mocAccountId, kind, limit, offset, tenantId]);

    const hospitals = useMemo(() => detail?.linkedHospitalDetails || [], [detail]);
    const doctors = activity?.doctors || [];

    // Default to the first link — linkedHospitalDetails is ordered by linkedAt
    // ASC, so that is where the patient started, which is the least surprising
    // hospital to land on.
    useEffect(() => {
        if (!detail || autoPickedFor.current === detail.mocAccountId) return;
        autoPickedFor.current = detail.mocAccountId;
        setTenantId(hospitals[0]?.tenantId || null);
    }, [detail, hospitals]);

    const currentHospital = useMemo(
        () => hospitals.find((h) => h.tenantId === tenantId) || null,
        [hospitals, tenantId]
    );

    // THE PERSON ID, and there is only one. `mocAccountId` IS the `patients`
    // row's `patientId` since one-patient-row — the same id at every hospital —
    // so every tab is handed this and the hospital picker changes only which
    // TENANT's clinical data is asked for.
    const personId = mocAccountId;

    // Load the person row once. It is passed explicitly because a super_admin
    // token carries no tenantId for the guard to scope by, and it no longer
    // depends on the picked hospital: the profile is the same answer whichever
    // hospital is selected.
    useEffect(() => {
        dispatch(getPatientById(personId));
    }, [dispatch, personId]);

    // A price is (patient, doctor) at ONE hospital, so a doctor picked at the
    // previous hospital means nothing at this one.
    useEffect(() => {
        setPriceDoctorId(null);
    }, [tenantId]);

    // Close the pickers on an outside click.
    useEffect(() => {
        const onClick = (event) => {
            if (mobileTabOpen && !event.target.closest('[data-testid~="moc-profile-mobile-tabs"]')) {
                setMobileTabOpen(false);
            }
            if (pickerOpen && !event.target.closest('[data-testid~="moc-hospital-picker"]')) {
                setPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [mobileTabOpen, pickerOpen]);

    // A new hospital selection restarts the timeline at page 1 — the old
    // offset was into a different hospital's row set.
    const pickHospital = (id) => {
        setTenantId(id);
        setOffset(0);
    };

    const handlePageChange = (page, newLimit) => {
        if (newLimit && newLimit !== limit) {
            setLimit(newLimit);
            setOffset(0);
        } else {
            setOffset((page - 1) * limit);
        }
    };

    const toggleStatus = async () => {
        if (!detail) return;
        const next = detail.status === 'active' ? 'inactive' : 'active';
        const { isConfirmed } = await Swal.fire({
            title: next === 'inactive' ? 'Deactivate this identity?' : 'Activate this identity?',
            // Deactivating locks the person out of every connected hospital at
            // once, which is exactly the part that is easy to under-estimate.
            text: next === 'inactive'
                ? 'They will not be able to sign in to the Mitra One Care patient app for any connected hospital. Their records are untouched.'
                : 'They will be able to sign in to the Mitra One Care patient app again.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: next === 'inactive' ? '#dc2626' : '#4ABDBD',
            cancelButtonColor: '#9DB3B1',
            confirmButtonText: next === 'inactive' ? 'Deactivate' : 'Activate',
        });
        if (!isConfirmed) return;

        const result = await dispatch(setMocPatientStatus({ mocAccountId, status: next }));
        if (setMocPatientStatus.fulfilled.match(result)) {
            toast.success(next === 'active' ? 'Account activated' : 'Account deactivated');
        } else {
            toast.warn(result.payload?.error || 'Failed to update status');
        }
    };

    if (detailLoading && !detail) {
        return (
            <div className="bg-white rounded-2xl shadow-md p-10 flex justify-center">
                <CommonLoader />
            </div>
        );
    }

    if (detailError && !detail) {
        return <ErrorPage error={{ status: 500 }} pageName={t('patientprofilepage.heading', 'Patient Profile')} />;
    }

    if (!detail) return null;

    const fullName = `${detail.name || ''} ${detail.surname || ''}`.trim();
    const hasHospital = Boolean(currentHospital);
    // Set when the API had to thin the record down to identity fields
    // (projectPatient). super_admin counts as connected (utils/patientAccess.js,
    // 26 Aug 2026), so this is a safety net rather than the normal case — but
    // a thinned record must still say so instead of rendering blanks that read
    // as data loss.
    const identityOnly = selectedPatient?.accessLevel === 'identity';
    const personLoaded = selectedPatient?.patientId === personId;

    // Only Custom Price needs a hospital of its own: a price is (patient,
    // doctor) AT one hospital. Profile and Dependencies belong to the person,
    // Bills and Platform activity answer across every hospital.
    const tabLocks = {
        [TAB_KEYS.price]: !hasHospital,
    };
    // The tabs the hospital picker actually steers. Rendering it above Profile
    // or Dependencies would imply those change with the selection; they do not.
    const CLINICAL_TABS = [TAB_KEYS.price, TAB_KEYS.activity];

    const tabs = [
        { key: TAB_KEYS.profile, label: t('patientprofilepage.tabs.profile', 'Profile'), testId: 'moc-tab-profile' },
        { key: TAB_KEYS.dependencies, label: t('patientprofilepage.tabs.dependencies', 'Dependencies'), testId: 'moc-tab-dependencies' },
        { key: TAB_KEYS.health, label: t('moc.tab_health_record', 'Health record'), testId: 'moc-tab-health-record' },
        { key: TAB_KEYS.price, label: t('patientprofilepage.tabs.custom_price', 'Custom Price'), testId: 'moc-tab-custom-price' },
        { key: TAB_KEYS.bills, label: t('moc.tab_bills', 'Bills'), testId: 'moc-tab-bills' },
        // Setting a password is a write (patients.manage); the tab is not offered
        // to a view-only platform staff member.
        ...(canManage ? [{ key: TAB_KEYS.password, label: t('patientprofilepage.tabs.password', 'Password'), testId: 'moc-tab-password' }] : []),
        { key: TAB_KEYS.activity, label: t('moc.tab_activity', 'Platform activity'), testId: 'moc-tab-activity' },
    ];

    const handleTabClick = (key) => {
        if (tabLocks[key]) {
            toast.warn(t('moc.tab_needs_hospital', 'No hospital has given this patient a number yet, so there is nothing to show here.'));
            return;
        }
        setActiveTab(key);
    };

    const noHospitalTitle = t('moc.no_hospital_title', 'No hospital yet');
    const noHospitalBody = t('moc.no_hospital_body',
        'This person has a Mitra One Care profile but no hospital has given them a patient number yet. Their profile and family are above; appointments, bills and prices are a hospital’s own records, so there are none until the first visit.');

    return (
        <div data-testid="moc-patient-detail" className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 sm:p-6 mx-auto">
            {/* Back + heading — the tenant profile's header, unchanged. */}
            <div className="mb-4 flex gap-2">
                <button
                    type="button"
                    data-testid="moc-patient-back-button"
                    onClick={() => navigate('/patients')}
                    className="bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white p-2 rounded-xl hover:from-[#3AA8A8] hover:to-[#2D8A8A] font-semibold transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl hover:scale-105"
                >
                    <ArrowLeftCircle className="h-5 w-5" />
                </button>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                    {t('patientprofilepage.heading', 'Patient Profile')}
                </h1>
            </div>

            {/* Identity strip: who this is. The hospital picker is NOT here any
                more — it steers the clinical tabs only, so it lives with them
                (see the header). */}
            <div className="mb-6 rounded-2xl border border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 p-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        {/* The person's ONE photo when they have one (presigned
                            by the API); the initial stays the fallback. */}
                        {detail.image ? (
                            <img
                                src={detail.image}
                                alt=""
                                className="w-12 h-12 rounded-full object-cover shadow-sm flex-shrink-0"
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] text-white font-bold text-xl flex items-center justify-center shadow-sm flex-shrink-0">
                                {(fullName || '?').charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div className="min-w-0">
                            <p className="text-lg font-bold text-gray-800 truncate">
                                {fullName || t('moc.unnamed', 'Unnamed identity')}
                            </p>
                            {/* No Mitra One Care id here: owner rule 29 Sep
                                2026, no system id is drawn on screen. The
                                hospital's own patient codes are in the
                                allocation chips below. */}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <MocStatusBadge status={detail.status} />
                        {canManage && (
                        <button
                            type="button"
                            data-testid="moc-toggle-status"
                            onClick={toggleStatus}
                            disabled={statusUpdating}
                            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition-colors cursor-pointer disabled:opacity-50 ${detail.status === 'active'
                                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                        >
                            {statusUpdating
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : detail.status === 'active' ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                            {detail.status === 'active' ? 'Deactivate account' : 'Activate account'}
                        </button>
                        )}
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap items-end gap-x-8 gap-y-3">
                    <div className="space-y-1.5">
                        <InfoRow icon={Mail}>
                            <span className="min-w-0 break-all">{detail.email || '—'}</span>
                            {detail.email && <VerifiedMark verified={detail.emailVerified} />}
                        </InfoRow>
                        <InfoRow icon={Phone}>
                            <span>{detail.phone || '—'}</span>
                            {detail.phone && <VerifiedMark verified={detail.phoneVerified} />}
                        </InfoRow>
                        <InfoRow icon={CalendarClock}>
                            <span className="text-gray-500">Joined {formatDate(detail.createdAt)}</span>
                        </InfoRow>
                    </div>

                    {/* Where this one profile is known, and under which number
                        each hospital knows it. Read-only: a hospital allocates
                        its own code, the platform never types one. */}
                    <div data-testid="moc-allocations" className="min-w-0">
                        <span className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">
                            {t('moc.allocations', 'Patient number at each hospital')}
                        </span>
                        {hospitals.length === 0 ? (
                            <span className="text-sm text-gray-500">{t('moc.no_hospitals', 'No hospital yet')}</span>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {hospitals.map((h) => (
                                    <span
                                        key={h.tenantId}
                                        data-testid={`moc-allocation-${h.tenantId}`}
                                        className="inline-flex items-center gap-1.5 rounded-xl border border-[#4ABDBD]/30 bg-[#4ABDBD]/10 px-2.5 py-1.5 text-xs text-[#2D8A8A]"
                                    >
                                        <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                        <span className="max-w-[12rem] truncate font-semibold">{h.hospitalName || t('moc.hr_detail_hospital', 'Hospital')}</span>
                                        {codeAt(h) && <span className="font-mono text-[10px] text-[#2D8A8A]/80">{codeAt(h)}</span>}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tab rail — the tenant profile's, pill for pill. */}
            <div className="mb-6">
                <div data-testid="moc-profile-tabs" className="hidden w-fit sm:flex flex-wrap justify-start gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200">
                    {tabs.map(({ key, label, testId }) => (
                        <button
                            key={key}
                            data-testid={testId}
                            onClick={() => handleTabClick(key)}
                            className={`relative px-6 py-3 text-sm font-semibold transition-colors duration-300 rounded-xl z-10 ${activeTab === key && !tabLocks[key]
                                ? 'text-white shadow-md'
                                : 'text-gray-600 hover:text-[#4ABDBD]'} ${tabLocks[key] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            {activeTab === key && !tabLocks[key] && (
                                <motion.div
                                    layoutId="activeMocPatientTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">{label}</span>
                        </button>
                    ))}
                </div>

                <div data-testid="moc-profile-mobile-tabs" className="sm:hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl shadow-lg border border-gray-200 p-2">
                        <div className="relative">
                            <button
                                type="button"
                                data-testid="moc-mobile-tab-selector"
                                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-gray-700 font-semibold cursor-pointer"
                                onClick={() => setMobileTabOpen(!mobileTabOpen)}
                            >
                                <span>{tabs.find((tab) => tab.key === activeTab)?.label}</span>
                                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${mobileTabOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {mobileTabOpen && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                                    {tabs.map(({ key, label, testId }) => (
                                        <button
                                            key={key}
                                            type="button"
                                            data-testid={`mobile-${testId}`}
                                            onClick={() => { handleTabClick(key); setMobileTabOpen(false); }}
                                            className={`w-full text-left px-4 py-3 text-sm font-semibold transition-all duration-300 border-b border-gray-100 last:border-b-0 ${activeTab === key && !tabLocks[key]
                                                ? 'text-white bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A]'
                                                : 'text-gray-600 hover:text-[#4ABDBD] hover:bg-gray-50'} ${tabLocks[key] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div data-testid="moc-profile-content" className="p-0 sm:p-4 sm:pb-0 pt-4">
                    {/* THE HOSPITAL PICKER, WITH THE TABS IT STEERS. Above the
                        clinical tabs only: Profile and Dependencies are the
                        person's and do not change with it, so a picker over
                        them would be a control that does nothing. */}
                    {CLINICAL_TABS.includes(activeTab) && (
                        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-gray-50/60 p-3">
                            <HospitalPicker
                                hospitals={hospitals}
                                current={currentHospital}
                                open={pickerOpen}
                                onToggle={() => setPickerOpen((v) => !v)}
                                onPick={(id) => { pickHospital(id); setPickerOpen(false); }}
                                label={t('moc.viewing_data_for', 'Hospital records from')}
                                noneLabel={t('moc.no_hospitals', 'No hospital yet')}
                                hospitalWord={t('moc.hr_detail_hospital', 'Hospital')}
                            />
                            <p className="min-w-0 flex-1 text-[11px] leading-relaxed text-gray-500">
                                {t('moc.clinical_scope_note', 'Appointments, prices and orders are a hospital’s own records. The profile and family above belong to the patient and are the same at every hospital.')}
                            </p>
                        </div>
                    )}

                    {activeTab === TAB_KEYS.profile && (
                        patientLoading && !personLoaded ? (
                            <div className="flex flex-col items-center justify-center py-4 pt-12">
                                <CommonLoader />
                                <span className="text-xl text-[#4ABDBD]">{t('patientprofilepage.loading', 'Loading…')}</span>
                            </div>
                        ) : (
                            <>
                                {/* WHY THERE IS NO EDIT BUTTON. The row is the
                                    patient's own Mitra One Care profile, which
                                    they maintain in their app; PUT /patients/:id
                                    answers 409 MOC_OWNED_FIELD on any change to
                                    a claimed person's identity, contact or
                                    profile field, whoever asks. Saying so beats
                                    an Edit button that ends in a refusal. */}
                                <div data-testid="moc-person-profile-banner" className="mb-6 flex items-start gap-3 rounded-xl border-l-4 border-[#4ABDBD] bg-[#4ABDBD]/10 p-4">
                                    <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#2D8A8A]" />
                                    <div>
                                        <p className="text-sm font-semibold text-[#2D8A8A]">
                                            {t('moc.person_profile_title', 'The patient’s Mitra One Care profile')}
                                        </p>
                                        <p className="mt-1 text-xs leading-relaxed text-[#2D8A8A]/90">
                                            {identityOnly
                                                ? t('moc.identity_only_body', 'Only the identity fields came back for this person, so the rest of the form is blank here rather than missing from their profile.')
                                                : t('moc.person_profile_body', 'One profile, every hospital they visit. The patient changes it in their own app, so it is read-only here — a hospital edits only its own details (assigned doctor, price, its note) from its admin app. To read a clinical record, use break-glass on the Platform activity tab, which asks for a reason and writes it to the audit log.')}
                                        </p>
                                    </div>
                                </div>
                                <PatientProfileTab patient={selectedPatient} readOnly />
                            </>
                        )
                    )}

                    {activeTab === TAB_KEYS.dependencies && (
                        <DependenciesReadOnly patientId={personId} />
                    )}

                    {/* Deep-linked with ?tab=price and no allocation: say why
                        rather than render an empty panel. */}
                    {/* The health record belongs to the PERSON and reads
                        the same at every hospital, so it is never locked
                        behind an allocation and never carries the picker. */}
                    {activeTab === TAB_KEYS.health && (
                        <HealthRecordReadOnly patientId={personId} />
                    )}

                    {activeTab === TAB_KEYS.price && !hasHospital && (
                        <NoHospitalNotice title={noHospitalTitle} body={noHospitalBody} />
                    )}

                    {activeTab === TAB_KEYS.price && hasHospital && (
                        <MocCustomPriceTab
                            patientId={personId}
                            hospitalName={currentHospital?.hospitalName || t('moc.hr_detail_hospital', 'Hospital')}
                            doctors={doctors.filter((d) => d.tenantId === tenantId)}
                            doctorId={priceDoctorId}
                            onDoctorChange={setPriceDoctorId}
                        />
                    )}

                    {/* BILLS SPAN EVERY HOSPITAL, and the picker is deliberately
                        not above them: GET /billing/patient/:patientId takes no
                        tenant and a super_admin token is unscoped, so what comes
                        back is every invoice raised for this person anywhere.
                        The tab says so rather than letting a selected hospital
                        name imply a filter that is not applied. */}
                    {activeTab === TAB_KEYS.bills && (
                        hospitals.length > 0
                            ? <MocBillsTab patientId={personId} />
                            : <NoHospitalNotice title={noHospitalTitle} body={noHospitalBody} />
                    )}

                    {activeTab === TAB_KEYS.password && (
                        <MocAccountPasswordTab account={detail} hospitals={hospitals} />
                    )}

                    {activeTab === TAB_KEYS.activity && (
                        <MocActivityPanel
                            mocAccountId={mocAccountId}
                            patientName={fullName}
                            hospitals={hospitals}
                            doctors={doctors}
                            activity={activity}
                            activityLoading={activityLoading}
                            activityError={activityError}
                            kind={kind}
                            onKindChange={(k) => { setKind(k); setOffset(0); }}
                            limit={limit}
                            offset={offset}
                            onPageChange={handlePageChange}
                            currentTenantId={tenantId}
                            onPickHospital={pickHospital}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
